/**
 * Hover popover anchored to a table cell, rendered through a portal into
 * `document.body` so no `.table-scroll` (overflow: auto) clips it, and
 * positioned by `placeAnchoredPopover` against the VIEWPORT: right when
 * there is room, left when there is not; below, or above at the bottom edge.
 * Width follows the content (one line per item) up to the viewport minus
 * 16 px; only a longer item wraps. Scrolling anything or resizing the
 * window re-measures. Hover-only — no focus, no Esc, so it stays off the
 * layer stack (`useLayer` is for things a user opens and must close).
 */
import { useLayoutEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { placeAnchoredPopover, type Placement } from "@/lib/anchored-popover";

const MARGIN = 8;

export function AnchoredHoverPopover({
  anchor,
  children,
}: {
  /** The cell the popover belongs to; null hides it. */
  anchor: HTMLElement | null;
  children: ReactNode;
}) {
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<Placement | null>(null);

  useLayoutEffect(() => {
    if (!anchor || !node) return;
    const measure = () => {
      const a = anchor.getBoundingClientRect();
      const r = node.getBoundingClientRect();
      setPos(
        placeAnchoredPopover(
          { left: a.left, right: a.right, top: a.top, bottom: a.bottom },
          { width: r.width, height: r.height },
          { width: window.innerWidth, height: window.innerHeight },
          { margin: MARGIN },
        ),
      );
    };
    measure();
    // Capture phase: the table's own scroll container does not bubble.
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [anchor, node]);

  if (!anchor) return null;
  return createPortal(
    <div
      ref={setNode}
      role="tooltip"
      className="anchored-popover"
      style={{
        position: "fixed",
        left: pos?.left ?? 0,
        top: pos?.top ?? 0,
        visibility: pos ? "visible" : "hidden",
        maxWidth: `calc(100vw - ${MARGIN * 2}px)`,
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
